import { api } from './api';
import { AgentRun, AgentRunStep, AgentSpec } from '../types';

export interface StartAgentRunRequest {
  documentId: string;
  agentId?: string;
}

export interface StartAgentRunResponse {
  runId: string;
  status: string;
}

export interface AgentRunListResponse {
  runs: AgentRun[];
  count: number;
}

export interface AgentRunStepsResponse {
  steps: AgentRunStep[];
}

export interface SubmitReviewRequest {
  action: 'approved' | 'overridden' | 'noted';
  notes?: string;
}

export interface AgentSpecListResponse {
  specs: AgentSpec[];
  count: number;
}

export interface AgentSpecVersionsResponse {
  versions: AgentSpec[];
}

export const agentRunsApi = {
  // Start a new agent run for a case
  start: async (caseId: string, request: StartAgentRunRequest): Promise<StartAgentRunResponse> => {
    return api.post<StartAgentRunResponse>(`/cases/${caseId}/agent-runs`, request);
  },

  // List agent runs for a case
  listForCase: async (caseId: string, status?: string): Promise<AgentRunListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const queryString = params.toString();
    return api.get<AgentRunListResponse>(`/cases/${caseId}/agent-runs${queryString ? `?${queryString}` : ''}`);
  },

  // Get agent run details
  get: async (runId: string): Promise<AgentRun> => {
    return api.get<AgentRun>(`/agent-runs/${runId}`);
  },

  // Get agent run steps
  getSteps: async (runId: string): Promise<AgentRunStepsResponse> => {
    return api.get<AgentRunStepsResponse>(`/agent-runs/${runId}/steps`);
  },

  // Submit review action
  submitReview: async (runId: string, request: SubmitReviewRequest): Promise<AgentRun> => {
    return api.put<AgentRun>(`/agent-runs/${runId}/review`, request);
  },
};

export const agentSpecsApi = {
  // List all agent specs (admin only)
  list: async (status?: string): Promise<AgentSpecListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const queryString = params.toString();
    return api.get<AgentSpecListResponse>(`/admin/agent-specs${queryString ? `?${queryString}` : ''}`);
  },

  // Get active spec for an agent
  get: async (agentId: string): Promise<AgentSpec> => {
    return api.get<AgentSpec>(`/admin/agent-specs/${agentId}`);
  },

  // List all versions of a spec
  listVersions: async (agentId: string): Promise<AgentSpecVersionsResponse> => {
    return api.get<AgentSpecVersionsResponse>(`/admin/agent-specs/${agentId}/versions`);
  },

  // Create new spec
  create: async (spec: Partial<AgentSpec>): Promise<AgentSpec> => {
    return api.post<AgentSpec>('/admin/agent-specs', spec);
  },

  // Update spec (creates new version)
  update: async (agentId: string, spec: Partial<AgentSpec>): Promise<AgentSpec> => {
    return api.put<AgentSpec>(`/admin/agent-specs/${agentId}`, spec);
  },

  // Change spec status
  updateStatus: async (agentId: string, status: string, version?: string): Promise<AgentSpec> => {
    return api.put<AgentSpec>(`/admin/agent-specs/${agentId}/status`, { status, version });
  },
};
